import React, { useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import TicketDetailsContent from './TicketDetailsContent';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { RootState, useAppDispatch } from '../../../store';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { fetchTicket } from '../../../store/actions/HelpAction';
import DetailFrame from '../../shared/Frames/DetailFrame';
import Badge from '../../shared/TailwindComponents/Badge';
import { PiTagSimpleLight, PiGearLight, PiCheckCircleLight, PiXCircleLight } from 'react-icons/pi';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, ticketDetailsTourSteps } from '../../shared/FeatureTour';
import Skeleton from '../../shared/TailwindComponents/Skeleton';

const TicketDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { ticketId } = useParams<{ ticketId: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { ticket, isLoading, error } = useSelector((state: RootState) => state.help);
  const { setBreadcrumbs } = useBreadcrumbs();

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'ticket-details',
    steps: ticketDetailsTourSteps,
    autoStartDelay: 800,
  });
    // Normalize ticket data to ensure consistent field names
  const normalizedTicket = useMemo(() => {
    return ticket
      ? {
          ...ticket,
          id: ticket.id,
          ticket_id: ticket.ticket_id || ticket.id,
          title: ticket.title || '',
          description: ticket.description || '',
          status: ticket.status || 'open',
          priority: ticket.priority || 'medium',
          attachments: ticket.attachments || [],
          created_at: ticket.created_at ? new Date(ticket.created_at) : null,
          updated_at: ticket.updated_at ? new Date(ticket.updated_at) : null,
        }
      : null;
  }, [ticket]);


    const formattedId = useMemo(() => {
    return String(normalizedTicket?.id || normalizedTicket?.ticket_id || '').padStart(6, '0');
  }, [normalizedTicket]);

  const breadcrumbs = useMemo(() => {
    if (normalizedTicket?.id || normalizedTicket?.ticket_id) {
      return [
        { name: 'Help', href: '/help', current: false },
        { name: 'Support', href: '/help/support', current: false },
        { name: `#${formattedId}`, href: `/help/support/${ticketId}`, current: true },
      ];
    }
    return [
      { name: 'Support', href: '/help/support', current: false },
      { name: 'Loading...', href: `/help/support/${ticketId}`, current: true },
    ];
  }, [normalizedTicket, ticketId, formattedId]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchTicket(ticketId));
    };
    fetchData();
  }, [ticketId, dispatch]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, [breadcrumbs]);

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return null;
      case 'in_progress':
        return <PiGearLight className="text-info-500" size={16} />;
      case 'closed':
        return <PiCheckCircleLight className="text-success-500" size={16} />;
      case 'pending_user':
        return <PiXCircleLight className="text-warning-500" size={16} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'gray' | 'yellow' | 'blue' | 'green' | 'orange' | 'red' => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'blue';
      case 'in_progress':
        return 'blue';
      case 'closed':
        return 'green';
      case 'pending_user':
        return 'orange';
      default:
        return 'blue';
    }
  };

  const getPriorityColorBadge = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  const subHeaderRight = useMemo(() => {
    if (!normalizedTicket) return null;

    return (
      <div className="flex items-center gap-3">
        {normalizedTicket.created_at && (
          <Badge color="gray" tooltip={t('Created')}>
            {format(normalizedTicket.created_at, 'MMM d, HH:mm')}
          </Badge>
        )}
        {normalizedTicket.updated_at && (
          <Badge color="gray" tooltip={t('Updated')}>
            {format(normalizedTicket.updated_at, 'MMM d, HH:mm')}
          </Badge>
        )}
        <div className="flex items-center gap-1">
          {getStatusIcon(normalizedTicket.status)}
          <Badge className='capitalize' color={getStatusColor(normalizedTicket.status)} tooltip={t('Status')}>
            {normalizedTicket.status || 'open'}
          </Badge>
        </div>
        <Badge className='capitalize' color={getPriorityColorBadge(normalizedTicket.priority)} tooltip={t('Priority')}>
          {normalizedTicket.priority || 'medium'}
        </Badge>
      </div>
    );
  }, [normalizedTicket, t]);

  if (isLoading) {
    return <Skeleton variant="detail" />;
  }

  if (error) {
    return (
      <DetailFrame
        breadcrumbs={[
          { name: 'Help', href: '/help', current: false },
          { name: 'Support', href: '/help/support', current: true },
        ]}
        showBackButton={true}
      >
        <div className="text-center text-danger-500 mt-10">
          <p>{error}</p>
        </div>
      </DetailFrame>
    );
  }

  if (!normalizedTicket) {
    return (
      <DetailFrame
        breadcrumbs={[
          { name: 'Help', href: '/help', current: false },
          { name: 'Support', href: '/help/support', current: true },
        ]}
        showBackButton={true}
      >
        <div className="text-center mt-10">
          <p>{t('Ticket not found')}</p>
        </div>
      </DetailFrame>
    );
  }

  return (
    <DetailFrame
      breadcrumbs={breadcrumbs}
      showBackButton={true}
      subHeaderRight={subHeaderRight}
    >
      <TicketDetailsContent ticket={normalizedTicket} />
      <FeatureTour
        steps={ticketDetailsTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </DetailFrame>
  );
};

export default TicketDetailsPage;
